import { useAuth } from "../../auth/AuthContext";
import { useI18n, type MsgKey } from "../../i18n";
import {
  OPERATIONS_NODES,
  REVIEW_NODES,
  SCOPE_NODE,
  STANDARD_SOURCE_NODES,
  type GuideNode,
  type GuideStage,
} from "./guideData";

const HEADINGS: Record<GuideStage, { titleKey: MsgKey; descKey: MsgKey }> = {
  standards: { titleKey: "sysGuideStageStandards", descKey: "sysGuideStageStandardsDesc" },
  review: { titleKey: "sysGuideStageReview", descKey: "sysGuideStageReviewDesc" },
  operations: { titleKey: "sysGuideStageOperations", descKey: "sysGuideStageOperationsDesc" },
};

export function GuideStagePanel({
  stage,
  onSelect,
}: {
  stage: GuideStage;
  onSelect: (node: GuideNode) => void;
}) {
  const { t } = useI18n();
  const { titleKey, descKey } = HEADINGS[stage];

  return (
    <section className="space-y-3">
      <header>
        <h3 className="text-sm font-bold text-brand-700">{t(titleKey)}</h3>
        <p className="mt-0.5 text-sm text-slate-500">{t(descKey)}</p>
      </header>

      {stage === "standards" ? (
        <div className="space-y-2">
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
            <p className="mb-2 text-xs font-semibold text-slate-400">{t("sysGuideSourcesGroup")}</p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {STANDARD_SOURCE_NODES.map((node) => (
                <NodeCard key={node.id} node={node} onSelect={onSelect} />
              ))}
            </div>
          </div>
          <Arrow labelKey="sysGuideFlowScope" />
          <NodeCard node={SCOPE_NODE} onSelect={onSelect} />
          <p className="text-xs text-slate-500">{t("sysGuideFlowSnapshot")}</p>
        </div>
      ) : null}

      {stage === "review" ? (
        <div className="space-y-1">
          {REVIEW_NODES.map((node, index) => (
            <div key={node.id}>
              {index > 0 ? <Arrow /> : null}
              <NodeCard node={node} onSelect={onSelect} />
            </div>
          ))}
        </div>
      ) : null}

      {stage === "operations" ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {OPERATIONS_NODES.map((node) => (
            <NodeCard key={node.id} node={node} onSelect={onSelect} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function Arrow({ labelKey }: { labelKey?: MsgKey }) {
  const { t } = useI18n();
  return (
    <div className="flex items-center justify-center gap-2 py-1">
      <span aria-hidden="true" className="text-sm leading-none text-slate-400">
        ↓
      </span>
      {labelKey ? <span className="text-xs text-slate-500">{t(labelKey)}</span> : null}
    </div>
  );
}

export function NodeCard({
  node,
  onSelect,
}: {
  node: GuideNode;
  onSelect: (node: GuideNode) => void;
}) {
  const { t } = useI18n();
  const { can } = useAuth();

  const body = (
    <>
      <span className="flex items-start justify-between gap-2">
        <span className="text-sm font-semibold text-slate-800">{t(node.labelKey)}</span>
        {node.to === null ? (
          <span className="shrink-0 rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-600">
            {t("sysGuideAutomatic")}
          </span>
        ) : null}
      </span>
      <span className="mt-1 block text-xs leading-relaxed text-slate-500">{t(node.descKey)}</span>
    </>
  );

  const base = "block h-full w-full rounded-lg border p-3 text-left transition";

  if (node.to === null) {
    return <div className={`${base} border-dashed border-slate-300 bg-white`}>{body}</div>;
  }

  if (node.perm && !can(node.perm)) {
    return (
      <div
        className={`${base} border-slate-200 bg-white opacity-60`}
        title={t("sysGuideLockedHint")}
      >
        <span className="mb-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
          {t("sysGuideLocked")}
        </span>
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(node)}
      title={t("sysGuideStartTour")}
      className={`${base} border-slate-200 bg-white hover:border-brand-500 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1`}
    >
      {body}
    </button>
  );
}
